import request from '../../utils/request';
import { Result } from '@/models/general';
import {
  MatchView,
  MatchListView,
  MatchListQuery,
  CreateMatchInput,
  UpdateMatchInput,
  CompleteMatchInput,
  UpdateMatchStatusInput,
  RescheduleMatchInput,
  UpdateLiveScoreInput,
  BulkImportPreviewView,
  MatchBulkImportResultView,
  BulkImportMatchesRequest,
  BulkImportAnalyzeResultView,
  CLAWBACK_CONSENT_HEADER,
} from '@/models/tournament';
import type { ClawbackConsentOption } from '@/models/tournament';

/** CO-921/CO-922 Round 2: HR's approval of a pay claw-back travels as a request header. */
function consentHeaders(opts?: ClawbackConsentOption) {
  return opts?.clawbackConsent ? { headers: { [CLAWBACK_CONSENT_HEADER]: opts.clawbackConsent } } : undefined;
}

export async function getMatches(query: MatchListQuery) {
  const response = await request.get<Result<MatchListView>>('Match/GetMatches', { params: query });
  return response.data;
}

/** Server clamps `PageSize` to 1..100 (`MatchService.GetMatches`). Asking for more is silently capped. */
const MATCH_PAGE_SIZE = 100;
/** Runaway guard: 100 pages x 100 matches is far past any real tournament season. */
const MATCH_MAX_PAGES = 100;

/**
 * Every match matching the query, not just the first server page. Same trap as
 * `getRefereesAllPages`: a caller that passes one large `pageSize` to fill a picker gets silently
 * capped at 100, and the matches past the cap become unselectable with no empty state to explain it.
 */
export async function getMatchesAllPages(query: Omit<MatchListQuery, 'page' | 'pageSize'> = {}) {
  const first = await getMatches({ ...query, page: 1, pageSize: MATCH_PAGE_SIZE });
  if (first.errors.length > 0 || !first.payload) return first;

  const items = [...first.payload.items];
  const totalPages = Math.min(first.payload.totalPages ?? 1, MATCH_MAX_PAGES);

  for (let page = 2; page <= totalPages; page++) {
    const next = await getMatches({ ...query, page, pageSize: MATCH_PAGE_SIZE });
    if (next.errors.length > 0 || !next.payload) return next; // surface the failure, never a half list
    if (next.payload.items.length === 0) break;
    items.push(...next.payload.items);
  }

  return {
    ...first,
    payload: { ...first.payload, items, page: 1, pageSize: items.length },
  };
}

export async function getMatch(id: number) {
  const response = await request.get<Result<MatchView>>(`Match/GetMatch/${id}`);
  return response.data;
}

export async function createMatch(input: CreateMatchInput) {
  const response = await request.post<Result<MatchView>>('Match/CreateMatch', input);
  return response.data;
}

export async function updateMatch(input: UpdateMatchInput, opts?: ClawbackConsentOption) {
  const response = await request.put<Result<MatchView>>('Match/UpdateMatch', input, consentHeaders(opts));
  return response.data;
}

export async function archiveMatch(id: number, opts?: ClawbackConsentOption) {
  const response = await request.post<Result<boolean>>(`Match/ArchiveMatch/${id}`, undefined, consentHeaders(opts));
  return response.data;
}

export async function completeMatch(input: CompleteMatchInput, opts?: ClawbackConsentOption) {
  const response = await request.post<Result<MatchView>>('Match/CompleteMatch', input, consentHeaders(opts));
  return response.data;
}

export async function updateMatchStatus(input: UpdateMatchStatusInput, opts?: ClawbackConsentOption) {
  const response = await request.post<Result<MatchView>>('Match/UpdateMatchStatus', input, consentHeaders(opts));
  return response.data;
}

export async function rescheduleMatch(input: RescheduleMatchInput) {
  const response = await request.post<Result<MatchView>>('Match/RescheduleMatch', input);
  return response.data;
}

export async function updateLiveScore(input: UpdateLiveScoreInput) {
  const response = await request.post<Result<MatchView>>('Match/UpdateLiveScore', input);
  return response.data;
}

export async function updateMatchAttachment(args: {
  matchId: number;
  file?: File | null;
  remove?: boolean;
}) {
  const fd = new FormData();
  fd.append('matchId', String(args.matchId));
  if (args.file) fd.append('file', args.file);
  fd.append('remove', String(!!args.remove));
  // C10: do not set Content-Type; axios handles the multipart boundary
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await request.post<Result<MatchView>>('Match/UpdateAttachment', fd);
      return response.data;
    } catch (err: unknown) {
      lastError = err;
      const e = err as { response?: { status?: number }; code?: string };
      const isRetryable = !e?.response?.status || (e.response.status >= 500 && e.response.status < 600) || e.code === 'ECONNABORTED' || e.code === 'ERR_NETWORK';
      if (!isRetryable || attempt === 1) break;
      await new Promise((r) => setTimeout(r, 1000 + attempt * 2000));
    }
  }
  throw lastError;
}

export async function parseExcelPreview(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  // C10: Do NOT set Content-Type header — Axios auto-sets multipart boundary
  const response = await request.post<Result<BulkImportPreviewView>>('Match/ParseExcelPreview', formData);
  return response.data;
}

export async function bulkImportMatches(data: BulkImportMatchesRequest, opts?: ClawbackConsentOption) {
  const response = await request.post<Result<MatchBulkImportResultView>>('Match/BulkImportMatches', data, consentHeaders(opts));
  return response.data;
}

export async function analyzeBulkImport(data: BulkImportMatchesRequest) {
  const response = await request.post<Result<BulkImportAnalyzeResultView>>('Match/AnalyzeBulkImport', data);
  return response.data;
}

/**
 * CO-644 — download the bulk-import template for the matches page.
 *
 * A separate file from `Match/DownloadTemplate`, which serves the legacy single-shot importer's
 * sheet. This one matches what the import wizard actually parses (DAY/FIELD/…/FORMAT/TYPE) and
 * carries a "How to use" sheet documenting every column.
 */
export async function downloadBulkImportTemplate() {
  const response = await request.get<ArrayBuffer>('Match/DownloadBulkImportTemplate', {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(
    new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'match-bulk-import-template.xlsx');
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 0);
}
